import type { DynamicToolUIPart, ToolUIPart } from 'ai'
import type { ReactNode } from 'react'
import { toolLabel } from '@llj/ai-service-interface/dist/entities/tool-catalogue.interface'
import { ConfirmCardStatus, isUncertainOutcome, readConfirmOutput, toolNameOf } from './confirm'
import { CREATE_ACTIVITY_RENDERER } from './CreateActivityCard'

// 寫入前確認的卡片外框（#1693）：標題、確認／取消、狀態列共用；內容依工具名對應呈現元件。
// 🔴 外框沒有任何一個字出自模型：內容來自工具的 prepare（display），狀態來自伺服器。

type AnyToolPart = ToolUIPart | DynamicToolUIPart

export interface ConfirmCardLabels {
  confirm: string
  cancel: string
  executing: string
  done: string
  notDone: string
  failed: string
  // 執行逾時：可能已在背後完成，不能說「沒有完成」
  uncertain: string
}

const DEFAULT_LABELS: ConfirmCardLabels = {
  confirm: '確認',
  cancel: '取消',
  executing: '處理中…',
  done: '已完成',
  notDone: '未執行',
  failed: '沒有完成',
  uncertain: '結果不確定，請到後台查看',
}

export interface ConfirmCardBodyProps {
  display: unknown
  // 執行成功時工具回的結果；其餘狀態為 undefined
  result: unknown
  status: ConfirmCardStatus
}

// 一支確認類工具的呈現：內容元件與（選填）按鈕、狀態用語
export interface ConfirmCardRenderer {
  Body: (props: ConfirmCardBodyProps) => ReactNode
  labels?: Partial<ConfirmCardLabels>
}

const isPlain = (value: unknown): value is string | number | boolean =>
  ['string', 'number', 'boolean'].includes(typeof value)

// 沒有登記呈現元件的工具：把 display 攤成欄位清單，至少看得到要做什麼
export function GenericConfirmBody({ display }: ConfirmCardBodyProps) {
  if (isPlain(display)) return <p className="confirm-card-body">{String(display)}</p>
  if (typeof display !== 'object' || display === null) return null
  return (
    <dl className="confirm-card-fields">
      {Object.entries(display).map(([key, value]) => (
        <div key={key}>
          <dt>{key}</dt>
          <dd>{isPlain(value) ? String(value) : JSON.stringify(value)}</dd>
        </div>
      ))}
    </dl>
  )
}

// 工具名 → 呈現元件。新的確認類工具在這裡登記自己的卡片內容，外框與狀態不必重寫
export const CONFIRM_CARD_RENDERERS: Record<string, ConfirmCardRenderer> = {
  createActivity: CREATE_ACTIVITY_RENDERER,
}

const rendererOf = (toolName: string): ConfirmCardRenderer =>
  CONFIRM_CARD_RENDERERS[toolName] ?? { Body: GenericConfirmBody }

interface ConfirmCardProps {
  part: AnyToolPart
  status: ConfirmCardStatus
  // 有別的事在進行（串流中、取消中）時兩顆按鈕都停用
  disabled: boolean
  onConfirm: () => void
  onCancel: () => void
}

const statusText = (
  status: ConfirmCardStatus, labels: ConfirmCardLabels, message: string, uncertain: boolean,
): string | null => {
  if (status === 'executed') return labels.done
  if (uncertain) return labels.uncertain
  if (status === 'cancelled' || status === 'interrupted') return labels.notDone
  if (status === 'failed') return message ? `${labels.failed}：${message}` : labels.failed
  return null
}

export function ConfirmCard({ part, status, disabled, onConfirm, onCancel }: ConfirmCardProps) {
  const toolName = toolNameOf(part)
  const { Body, labels: custom } = rendererOf(toolName)
  const labels = { ...DEFAULT_LABELS, ...custom }
  const { display, result, message, error } = readConfirmOutput(part)
  const uncertain = status === 'failed' && isUncertainOutcome(error)
  const settled = statusText(status, labels, message, uncertain)
  const actionable = status === 'pending' || status === 'executing'
  return (
    <div className="row assistant">
      <section
        className={`confirm-card ${status}${uncertain ? ' uncertain' : ''}`}
        aria-label={`${toolLabel(toolName)}確認`}
      >
        <p className="confirm-card-title">{toolLabel(toolName)}</p>
        <Body display={display} result={status === 'executed' ? result : undefined} status={status} />
        {actionable ? (
          <div className="confirm-card-actions">
            <button
              type="button" className="confirm-card-cancel"
              disabled={disabled || status === 'executing'} onClick={onCancel}
            >
              {labels.cancel}
            </button>
            <button
              type="button" className="confirm-card-confirm"
              disabled={disabled || status === 'executing'} onClick={onConfirm}
            >
              {status === 'executing' ? labels.executing : labels.confirm}
            </button>
          </div>
        ) : (
          <p className="confirm-card-status" role="status">{settled}</p>
        )}
      </section>
    </div>
  )
}
